import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { getCategory, useScenarios } from '../store/contentStore';
import { getBundlesByCategory } from '../data/bundles';
import { useContentAccess } from '../hooks/useContentAccess';
import GuestGate from '../components/GuestGate';
import type { AgeGroup } from '../types';

const AGE_COLORS: Record<AgeGroup, string> = {
  '4-6': 'bg-green-100 text-green-700',
  '7-9': 'bg-blue-100 text-blue-700',
  '10-12': 'bg-purple-100 text-purple-700',
  all: 'bg-gray-100 text-gray-600',
};

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { canViewCard } = useContentAccess();
  const [showGuestGate, setShowGuestGate] = useState(false);

  const allScenarios = useScenarios();
  const category = categoryId ? getCategory(categoryId) : undefined;
  const bundles = categoryId ? getBundlesByCategory(categoryId) : [];
  const scenarios = allScenarios.filter((s) => s.categoryId === categoryId);

  if (!category) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <div className="text-5xl mb-4">😕</div>
        <h2 className="font-display text-xl font-bold text-gray-700 mb-2">
          Category not found
        </h2>
        <button
          onClick={() => navigate('/library')}
          className="mt-4 text-primary-600 font-semibold text-sm"
        >
          Back to Library
        </button>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-cream pb-24">
      {/* Header gradient */}
      <div className={`bg-gradient-to-br ${category.color} px-4 pt-12 pb-8`}>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-white/80 hover:text-white mb-4 text-sm font-semibold transition-colors"
        >
          <ChevronLeft size={18} />
          Back
        </button>

        <div className="text-5xl mb-3">{category.icon}</div>
        <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-2">
          {category.name}
        </h1>
        <p className="text-white/80 text-sm leading-relaxed">
          {category.description}
        </p>
        <div className="flex gap-3 mt-4">
          <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
            {bundles.length} bundle{bundles.length !== 1 ? 's' : ''}
          </span>
          <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
            {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className="px-4 pt-6 max-w-4xl mx-auto space-y-7">
        {/* Bundles section */}
        {bundles.length > 0 && (
          <section>
            <h2 className="font-display font-bold text-gray-800 text-base mb-3">
              Practice Bundles
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none">
              {bundles.map((bundle, i) => (
                <motion.button
                  key={bundle.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.07 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => navigate(`/library/bundle/${bundle.id}`)}
                  className="flex-shrink-0 w-48 text-left rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-200"
                >
                  <div
                    className={`bg-gradient-to-br ${bundle.color} p-4 h-full`}
                  >
                    <div className="text-3xl mb-2">{bundle.icon}</div>
                    <h3 className="font-display font-bold text-white text-sm leading-tight mb-1">
                      {bundle.name}
                    </h3>
                    <p className="text-white/70 text-xs line-clamp-2 mb-2">
                      {bundle.description}
                    </p>
                    <span className="bg-white/20 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                      {bundle.scenarioIds.length} cards
                    </span>
                  </div>
                </motion.button>
              ))}
            </div>
          </section>
        )}

        {/* Scenarios section */}
        <section>
          <h2 className="font-display font-bold text-gray-800 text-base mb-3">
            All Scenarios
          </h2>

          {scenarios.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <div className="text-4xl mb-2">📭</div>
              <p className="font-semibold text-gray-500 text-sm">
                No scenarios yet
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {scenarios.map((scenario, i) => {
                const locked = !canViewCard(i);
                return locked ? (
                  <motion.div
                    key={scenario.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => setShowGuestGate(true)}
                    className="relative cursor-pointer bg-white rounded-2xl p-4 shadow-card overflow-hidden"
                  >
                    <div className="blur-sm opacity-40 pointer-events-none flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center text-xl flex-shrink-0`}>
                        {category.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold text-gray-800 text-sm leading-tight mb-1">
                          {scenario.title}
                        </h3>
                        <p className="text-gray-400 text-xs italic">"{scenario.memoryPhrase}"</p>
                      </div>
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                      <Lock size={20} className="text-primary-600" />
                      <span className="text-xs font-bold text-primary-700">Sign up free</span>
                    </div>
                  </motion.div>
                ) : (
                  <motion.button
                    key={scenario.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate(`/library/scenario/${scenario.id}`)}
                    className="w-full text-left bg-white rounded-2xl p-4 shadow-card hover:shadow-card-hover transition-all duration-200 flex items-start gap-4"
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center text-xl flex-shrink-0`}>
                      {category.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-display font-bold text-gray-800 text-sm leading-tight">
                          {scenario.title}
                        </h3>
                        <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${AGE_COLORS[scenario.ageGroup]}`}>
                          {scenario.ageGroup}
                        </span>
                      </div>
                      <p className="text-gray-400 text-xs line-clamp-1 italic">
                        "{scenario.memoryPhrase}"
                      </p>
                    </div>
                    <ChevronRight size={15} className="text-gray-300 flex-shrink-0 mt-1" />
                  </motion.button>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
    <GuestGate visible={showGuestGate} onDismiss={() => setShowGuestGate(false)} />
    </>
  );
}
